import { ThreeItemKeys } from '../../../constants/three'
import { getObjectByUserData, getObjectsByUserData } from '../../../helpers/scene-json'
import { createMutation, createQuery } from '@tanstack/svelte-query'
import { get } from 'svelte/store'
import { storeScene } from '../../../stores/scene'
import { focusOnObject } from '../../../stores/camera'
import queryClient from '../../../constants/query-client'
import { pruneMissingFromMultiSelection } from '../../../stores/canvas-selection'

const useGroupQueries = () => {
  const getGroup = (groupId: string) => {
    const scene = get(storeScene)

    const foundGroup = getObjectByUserData(scene, ThreeItemKeys.GROUP_ID, groupId)
    if (!foundGroup) {
      throw new Error('group not found')
    }

    return foundGroup
  }
  const getGroupFiles = (groupId: string) => {
    const scene = get(storeScene)

    return getObjectsByUserData(scene, ThreeItemKeys.ASSIGNED_GROUP_ID, groupId)
  }

  const invalidateGroups = () => {
    queryClient.invalidateQueries({ queryKey: ['canvas', 'groups'] })
  }

  const useGroup = (groupId: string) => createQuery(() => ({
    queryKey: ['canvas', 'groups', groupId],
    queryFn: () => getGroup(groupId),
  }), () => queryClient)

  const useGroupFiles = (groupId: string) => createQuery(() => ({
    queryKey: ['canvas', 'groups', groupId, 'files'],
    queryFn: () => getGroupFiles(groupId),
  }), () => queryClient)

  const changeVisibility = (visibility: boolean) => createMutation(() => ({
    mutationFn: (groupId: string) => new Promise((resolve) => {
      const group = getGroup(groupId)
      group.visible = visibility
      resolve(group)
    }),
    onSuccess: invalidateGroups,
  }), () => queryClient)

  const hideMutation = changeVisibility(false)
  const showMutation = changeVisibility(true)

  const changeLockStatus = (newLockStatus: boolean) => createMutation(() => ({
    mutationFn: (groupId: string) => new Promise((resolve) => {
      const group = getGroup(groupId)
      group.userData[ThreeItemKeys.LOCKED] = newLockStatus
      resolve(group)
    }),
    onSuccess: invalidateGroups,
  }), () => queryClient)

  const unlockMutation = changeLockStatus(false)
  const lockMutation = changeLockStatus(true)

  const removeMutation = createMutation(() => ({
    mutationFn: (groupId: string) => new Promise((resolve) => {
      const group = getGroup(groupId)
      group?.parent?.remove(group)
      resolve(true)
    }),
    onSuccess: () => {
      pruneMissingFromMultiSelection()
      invalidateGroups()
    },
  }), () => queryClient)

  const renameMutation = createMutation(() => ({
    mutationFn: ({ groupId, name }: { groupId: string, name: string }) => new Promise((resolve) => {
      const group = getGroup(groupId)
      group.name = name
      resolve(true)
    }),
    onSuccess: invalidateGroups,
  }), () => queryClient)

  const assignToPrinterMutation = createMutation(() => ({
    mutationFn: ({ groupId, printerId }: { groupId: string, printerId: string | undefined }) => new Promise((resolve) => {
      const group = getGroup(groupId)
      group.userData[ThreeItemKeys.ASSIGNED_PRINTER_ID] = printerId
      resolve(group)
    }),
    onSuccess: invalidateGroups,
  }), () => queryClient)

  const focusCamera = (groupId: string) => {
    const scene = get(storeScene)
    const group = getObjectByUserData(scene, ThreeItemKeys.GROUP_ID, groupId)
    if (group) focusOnObject(group)
  }

  return {
    assignToPrinterMutation,
    focusCamera,
    hideMutation,
    lockMutation,
    removeMutation,
    renameMutation,
    showMutation,
    unlockMutation,
    useGroup,
    useGroupFiles,
  }
}

export default useGroupQueries
